import React, { useState, useCallback, useRef } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Label } from "@shadcn-in-nx/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@shadcn-in-nx/ui/alert-dialog";
import { Loader2, Clock, CheckCircle, AlertCircle } from "lucide-react";

const roundToQuarter = (v) => Math.round(parseFloat(v) * 4) / 4;

export default function HourLogForm({ subscriptionId, remainingHours, onSuccess }) {
    const [hours, setHours] = useState("");
    const [description, setDescription] = useState("");
    const [notes, setNotes] = useState("");
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);

    const { call: deductHours, isLoading } = useBluelagoon({ method: "deductSupportHours" });
    const deductRef = useRef(deductHours);
    deductRef.current = deductHours;

    const roundedHours = hours ? roundToQuarter(hours) : 0;

    const handleSubmit = (e) => {
        e.preventDefault();
        setSuccess(null);
        setError(null);
        if (!hours || !description.trim()) { setError("Hours and description are required"); return; }
        if (isNaN(roundedHours) || roundedHours <= 0) { setError("Hours must be a positive number"); return; }
        if (roundedHours > remainingHours) { setError(`Only ${remainingHours}h remaining (${roundedHours}h after rounding)`); return; }
        setShowConfirm(true);
    };

    const handleConfirm = useCallback(async () => {
        setShowConfirm(false);
        try {
            const res = await deductRef.current({ subscriptionId, hours: roundedHours, description: description.trim(), notes: notes.trim() || undefined });
            if (res?.data) {
                setSuccess(`Logged ${res.data.hoursDeducted}h - ${res.data.remainingHours}h remaining`);
                setHours(""); setDescription(""); setNotes("");
                onSuccess?.();
            }
        } catch (err) {
            setError(err?.error || "Failed to log hours");
        }
    }, [subscriptionId, roundedHours, description, notes, onSuccess]);

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg"><Clock className="h-5 w-5" />Log Hours</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                            <div className="space-y-1">
                                <Label>Hours (0.25 increments)</Label>
                                <Input type="number" min="0.25" step="0.25" max={remainingHours} value={hours} onChange={e => setHours(e.target.value)} placeholder="e.g. 1.5" />
                            </div>
                            <div className="space-y-1 sm:col-span-3">
                                <Label>Task Description</Label>
                                <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="What work was performed?" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label>Notes (optional)</Label>
                            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional details..." rows={2} />
                        </div>
                        {error && <div className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="h-4 w-4" />{error}</div>}
                        {success && <div className="flex items-center gap-2 text-green-600 text-sm"><CheckCircle className="h-4 w-4" />{success}</div>}
                        <Button type="submit" disabled={isLoading || !hours || !description.trim()}>
                            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Log Hours
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Hours Log</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <p>Deduct <strong>{roundedHours}h</strong> from this subscription?</p>
                            <p className="text-sm">Task: {description}</p>
                            <p className="text-sm text-muted-foreground">Remaining after: {(remainingHours - roundedHours).toFixed(2)}h</p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirm}>Confirm</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
